/**
 * ProfileCompleteGate — post-login blocker until the shipping profile is
 * 100% complete: first/last name, phone, street, city, state, ZIP.
 * Required before joining drops, challenges, games, or BLK Point top-ups
 * (enforced server-side too). Cannot be dismissed until complete.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertCircle, Loader2, Truck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const inputCls = "bg-black/60 border border-white/10 rounded-xl px-3 py-3 text-white text-sm focus:border-amber-400 focus:outline-none w-full";

const ProfileCompleteGate = ({ user, token, onComplete }) => {
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", address_line1: "", address_line2: "", city: "", state: "", postal_code: "" });
  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user || !token || user.is_admin) { setVisible(false); return; }
    const ship = (user.shipping_address && typeof user.shipping_address === "object")
      ? user.shipping_address : {};
    const vals = {
      first_name: (ship.first_name || "").trim(),
      last_name: (ship.last_name || "").trim(),
      phone: (user.phone || "").trim(),
      address_line1: (ship.address_line1 || "").trim(),
      address_line2: (ship.address_line2 || "").trim(),
      city: (ship.city || "").trim(),
      state: (ship.state || "").trim(),
      postal_code: (ship.postal_code || "").trim(),
    };
    setForm(vals);
    const complete = vals.first_name && vals.last_name && vals.phone && vals.address_line1 && vals.city && vals.state && vals.postal_code;
    setVisible(!complete);
  }, [user, token]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const allFilled = form.first_name.trim() && form.last_name.trim() && form.phone.trim() && form.address_line1.trim() && form.city.trim() && form.state.trim() && form.postal_code.trim();

  const save = async (e) => {
    e?.preventDefault?.();
    if (!allFilled) { toast.error("All fields except Apt/Unit are required."); return; }
    setSaving(true);
    try {
      const existing = (user.shipping_address && typeof user.shipping_address === "object") ? user.shipping_address : {};
      await axios.put(
        `${API}/profile`,
        {
          phone: form.phone.trim(),
          shipping_address: {
            ...existing,
            first_name: form.first_name.trim(),
            last_name: form.last_name.trim(),
            address_line1: form.address_line1.trim(),
            address_line2: form.address_line2.trim(),
            city: form.city.trim(),
            state: form.state.trim(),
            postal_code: form.postal_code.trim(),
            country: existing.country || "US",
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("You're 100% shipping-verified — drops unlocked!");
      setVisible(false);
      onComplete?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save — try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[300] bg-black/90 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
      data-testid="profile-complete-gate"
    >
      <form
        onSubmit={save}
        className="w-full sm:max-w-md bg-zinc-950 border-2 border-amber-500/40 rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl my-auto"
      >
        <div className="flex items-start gap-3 mb-4">
          <Truck className="text-amber-300 flex-shrink-0 mt-1" size={26} />
          <div>
            <h2 className="text-white font-black text-lg uppercase tracking-wider leading-tight">Complete your shipping profile</h2>
            <p className="text-white/60 text-xs mt-1">Required before you can join drops, challenges, games, or top up BLK Points. We ship real packs — we need the full picture.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <input type="text" value={form.first_name} onChange={set("first_name")} placeholder="First name *" autoComplete="given-name" className={inputCls} required data-testid="profile-gate-first-name" />
          <input type="text" value={form.last_name} onChange={set("last_name")} placeholder="Last name *" autoComplete="family-name" className={inputCls} required data-testid="profile-gate-last-name" />
        </div>
        <div className="mb-3">
          <input type="tel" value={form.phone} onChange={set("phone")} placeholder="Phone number *" autoComplete="tel" className={inputCls} required data-testid="profile-gate-phone" />
        </div>
        <div className="mb-3">
          <input type="text" value={form.address_line1} onChange={set("address_line1")} placeholder="Street address *" autoComplete="address-line1" className={inputCls} required data-testid="profile-gate-address1" />
        </div>
        <div className="mb-3">
          <input type="text" value={form.address_line2} onChange={set("address_line2")} placeholder="Apt / Unit (optional)" autoComplete="address-line2" className={inputCls} data-testid="profile-gate-address2" />
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <input type="text" value={form.city} onChange={set("city")} placeholder="City *" autoComplete="address-level2" className={inputCls} required data-testid="profile-gate-city" />
          <input type="text" value={form.state} onChange={set("state")} placeholder="State *" autoComplete="address-level1" className={inputCls} required data-testid="profile-gate-state" />
          <input type="text" value={form.postal_code} onChange={set("postal_code")} placeholder="ZIP *" autoComplete="postal-code" className={inputCls} required data-testid="profile-gate-zip" />
        </div>

        <button
          type="submit"
          disabled={saving || !allFilled}
          className="w-full brand-gradient-bg text-black font-black py-3 rounded-xl uppercase tracking-widest text-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="profile-gate-save"
        >
          {saving && <Loader2 size={16} className="animate-spin" />}
          Save & Unlock Drops
        </button>

        <p className="text-white/30 text-[10px] text-center mt-3 flex items-center justify-center gap-1">
          <AlertCircle size={10} /> US shipping only — you can edit this anytime in Profile.
        </p>
      </form>
    </div>
  );
};

export default ProfileCompleteGate;
